import { useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';

interface ContentProtectionOptions {
  watermarkText?: string;
  detectScreenshot?: boolean;
  disableRightClick?: boolean;
  disableDevTools?: boolean;
  onScreenshotDetected?: () => void;
}

/**
 * Hook to implement content protection measures
 * NOTE: These are deterrents, not foolproof protection
 */
export const useContentProtection = (options: ContentProtectionOptions = {}) => {
  const {
    watermarkText = '',
    detectScreenshot = true,
    disableRightClick = true,
    disableDevTools = false,
    onScreenshotDetected
  } = options;

  const lastVisibilityChange = useRef<number>(0);

  // Disable right-click context menu
  useEffect(() => {
    if (!disableRightClick) return;

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      toast.error('Right-click is disabled on this page', {
        duration: 2000,
        id: 'right-click-disabled'
      });
      return false;
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, [disableRightClick]);

  // Detect potential screenshot attempts
  useEffect(() => {
    if (!detectScreenshot) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Detect common screenshot shortcuts
      const isScreenshot = 
        // Mac: Cmd+Shift+3, Cmd+Shift+4, Cmd+Shift+5
        (e.metaKey && e.shiftKey && ['3', '4', '5'].includes(e.key)) ||
        // Windows: PrtScn, Alt+PrtScn, Win+PrtScn
        (e.key === 'PrintScreen') ||
        // Windows Snipping Tool: Win+Shift+S
        (e.metaKey && e.shiftKey && e.key === 's');

      if (isScreenshot) {
        console.warn('Screenshot attempt detected');
        
        // Log the attempt (you could send this to your backend)
        if (onScreenshotDetected) {
          onScreenshotDetected();
        }

        toast.error('Screenshots are not permitted. This action has been logged.', {
          duration: 4000,
          icon: '⚠️'
        });
      }
    };

    // Detect when user switches away from the page (might be taking screenshot)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        lastVisibilityChange.current = Date.now();
      } else {
        const timeDiff = Date.now() - lastVisibilityChange.current;
        // If user was away for less than 2 seconds, might be taking screenshot
        if (timeDiff < 2000 && timeDiff > 100) {
          console.warn('Possible screenshot detected (visibility change)');
          if (onScreenshotDetected) {
            onScreenshotDetected();
          }
        }
      }
    };

    // Detect window blur (switching to screenshot tool)
    const handleBlur = () => {
      lastVisibilityChange.current = Date.now();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  }, [detectScreenshot, onScreenshotDetected]);

  // Attempt to detect dev tools (very limited effectiveness)
  useEffect(() => {
    if (!disableDevTools) return;

    let devtoolsOpen = false;
    const threshold = 160;

    const detectDevTools = () => {
      const widthThreshold = window.outerWidth - window.innerWidth > threshold;
      const heightThreshold = window.outerHeight - window.innerHeight > threshold;

      if (widthThreshold || heightThreshold) {
        if (!devtoolsOpen) {
          devtoolsOpen = true;
          toast.error('Developer tools detected. Please close them to continue.', {
            duration: 5000
          });
        }
      } else {
        devtoolsOpen = false;
      }
    };

    const interval = setInterval(detectDevTools, 1000);
    return () => clearInterval(interval);
  }, [disableDevTools]);

  return {
    watermarkText
  };
};

// Utility to log screenshot attempts to backend
export const logScreenshotAttempt = async (userId: string, page: string) => {
  try {
    // You can implement backend logging here
    console.log('Screenshot attempt logged:', { userId, page, timestamp: new Date().toISOString() });
    
    // Example: Send to your backend
    // await fetch('/api/security/log-screenshot', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ userId, page, timestamp: new Date().toISOString() })
    // });
  } catch (error) {
    console.error('Failed to log screenshot attempt:', error);
  }
};
